import React, { useEffect, useState } from 'react';
import caseService from '../../services/caseService';
import api from '../../services/api';
import { formatDate, formatForInput } from '../../utils/dateUtils';
import { FileCode, Info, Pencil, Trash2, X, Check, Wallet, Plus, Clock, DollarSign, ChevronDown, ChevronUp, FileText, CreditCard, Receipt, Eye, Send } from 'lucide-react';
import CreateInvoiceModal from '../invoices/CreateInvoiceModal';

const statusColors = {
  draft: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  sent: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  viewed: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  partial: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  paid: 'bg-green-500/20 text-green-300 border-green-500/30',
  overdue: 'bg-red-500/20 text-red-300 border-red-500/30',
  cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const CaseTime = ({ caseId, onRefresh }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [utbmsCodes, setUtbmsCodes] = useState({ task_codes: [], activity_codes: [] });
  const [formData, setFormData] = useState({
    description: '',
    hours: '',
    billable: true,
    date: formatForInput(new Date()),
    utbms_task_code: '',
    utbms_activity_code: '',
  });
  const [editFormData, setEditFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Prepaid hours state
  const [prepaidInfo, setPrepaidInfo] = useState(null);
  const [prepaidLoading, setPrepaidLoading] = useState(true);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [purchaseFormData, setPurchaseFormData] = useState({
    hours: '',
    hourly_rate: '',
    description: 'Prepaid hours purchase',
  });
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);

  // Invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceInitialData, setInvoiceInitialData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Case invoices & payments state
  const [caseInvoices, setCaseInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [invoicePayments, setInvoicePayments] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'check',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: '',
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

  useEffect(() => {
    loadEntries();
    loadUtbmsCodes();
    loadPrepaidInfo();
    loadCaseInvoices();
  }, [caseId]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await caseService.getTimeEntries(caseId);
      setEntries(data);
    } catch (err) {
      console.error('Failed to load time entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUtbmsCodes = async () => {
    try {
      const data = await caseService.getUtbmsCodes();
      setUtbmsCodes({
        task_codes: data.task_codes || [],
        activity_codes: data.activity_codes || []
      });
    } catch (err) {
      console.error('Failed to load UTBMS codes:', err);
    }
  };

  const loadPrepaidInfo = async () => {
    try {
      setPrepaidLoading(true);
      const data = await caseService.getPrepaidInfo(caseId);
      setPrepaidInfo(data);
    } catch (err) {
      console.error('Failed to load prepaid info:', err);
      setPrepaidInfo(null);
    } finally {
      setPrepaidLoading(false);
    }
  };

  const loadCaseInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const response = await api.get(`/invoices/?case_id=${caseId}&page_size=100`);
      setCaseInvoices(response.data.invoices || []);
    } catch (err) {
      console.error('Failed to load case invoices:', err);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const loadInvoicePayments = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/payments`);
      setInvoicePayments(prev => ({ ...prev, [invoiceId]: response.data }));
    } catch (err) {
      console.error('Failed to load payments:', err);
    }
  };

  const toggleInvoiceExpand = (invoiceId) => {
    if (expandedInvoice === invoiceId) {
      setExpandedInvoice(null);
    } else {
      setExpandedInvoice(invoiceId);
      if (!invoicePayments[invoiceId]) {
        loadInvoicePayments(invoiceId);
      }
    }
  };

  const openPaymentModal = (invoice) => {
    setPaymentInvoice(invoice);
    setPaymentForm({
      amount: invoice.amount_due?.toString() || '',
      payment_method: 'check',
      payment_date: new Date().toISOString().split('T')[0],
      reference_number: '',
      notes: '',
    });
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentInvoice) return;
    const amount = parseFloat(paymentForm.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }
    try {
      setSubmittingPayment(true);
      await api.post(`/invoices/${paymentInvoice.id}/payments`, {
        amount,
        payment_method: paymentForm.payment_method,
        payment_date: paymentForm.payment_date,
        reference_number: paymentForm.reference_number || null,
        notes: paymentForm.notes || null,
      });
      setShowPaymentModal(false);
      setPaymentInvoice(null);
      loadCaseInvoices();
      if (expandedInvoice === paymentInvoice.id) {
        loadInvoicePayments(paymentInvoice.id);
      }
      onRefresh?.();
    } catch (err) {
      alert('Failed to record payment: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handlePurchasePrepaid = async (e) => {
    e.preventDefault();
    try {
      setPurchaseSubmitting(true);
      await caseService.purchasePrepaidHours(caseId, {
        hours: parseFloat(purchaseFormData.hours),
        hourly_rate: parseFloat(purchaseFormData.hourly_rate),
        description: purchaseFormData.description || 'Prepaid hours purchase',
      });
      setPurchaseFormData({
        hours: '',
        hourly_rate: '',
        description: 'Prepaid hours purchase',
      });
      setShowPurchaseForm(false);
      loadPrepaidInfo();
    } catch (err) {
      alert('Failed to add prepaid hours: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await caseService.addTimeEntry(caseId, {
        ...formData,
        hours: parseFloat(formData.hours),
        utbms_task_code: formData.utbms_task_code || null,
        utbms_activity_code: formData.utbms_activity_code || null,
      });
      setFormData({
        description: '',
        hours: '',
        billable: true,
        date: formatForInput(new Date()),
        utbms_task_code: '',
        utbms_activity_code: '',
      });
      setShowAddForm(false);
      loadEntries();
      loadPrepaidInfo();
      onRefresh?.();
    } catch (err) {
      alert('Failed to add time entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry.id);
    setEditFormData({
      description: entry.description,
      hours: entry.hours.toString(),
      billable: entry.billable,
      date: formatForInput(new Date(entry.date)),
      utbms_task_code: entry.utbms_task_code || '',
      utbms_activity_code: entry.utbms_activity_code || '',
      hourly_rate: entry.rate?.toString() || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditFormData({});
  };

  const handleSaveEdit = async (entryId) => {
    try {
      setSubmitting(true);
      await caseService.updateTimeEntry(caseId, entryId, {
        description: editFormData.description,
        hours: parseFloat(editFormData.hours),
        billable: editFormData.billable,
        date: editFormData.date,
        utbms_task_code: editFormData.utbms_task_code || null,
        utbms_activity_code: editFormData.utbms_activity_code || null,
        hourly_rate: editFormData.hourly_rate ? parseFloat(editFormData.hourly_rate) : null,
      });
      setEditingEntry(null);
      setEditFormData({});
      loadEntries();
      loadPrepaidInfo();
      onRefresh?.();
    } catch (err) {
      alert('Failed to update time entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    try {
      await caseService.deleteTimeEntry(caseId, entryId);
      loadEntries();
      loadPrepaidInfo();
      onRefresh?.();
    } catch (err) {
      alert('Failed to delete time entry');
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      setInvoiceLoading(true);
      const unbilledData = await caseService.getUnbilledItems(caseId);

      // Check if there are unbilled items
      if (unbilledData.time_entries.length === 0 && unbilledData.expenses.length === 0) {
        alert('No unbilled time entries or expenses found for this case.');
        return;
      }

      // Build initial line items from unbilled time entries and expenses
      const initialLineItems = [];

      // Add time entries as line items
      unbilledData.time_entries.forEach(entry => {
        initialLineItems.push({
          description: entry.description || 'Time entry',
          quantity: entry.hours,
          unit_price: entry.rate || 0,
          time_entry_id: entry.id,
          expense_id: null
        });
      });

      // Add expenses as line items
      unbilledData.expenses.forEach(expense => {
        initialLineItems.push({
          description: expense.description || `Expense: ${expense.expense_type || 'Other'}`,
          quantity: 1,
          unit_price: expense.amount || 0,
          time_entry_id: null,
          expense_id: expense.id
        });
      });

      // Set the initial data for the modal
      setInvoiceInitialData({
        caseId: unbilledData.case_id,
        contactId: unbilledData.contact_id,
        contactName: unbilledData.contact_name,
        lineItems: initialLineItems
      });

      setShowInvoiceModal(true);
    } catch (err) {
      console.error('Failed to load unbilled items:', err);
      alert('Failed to load unbilled items: ' + (err.response?.data?.detail || err.message));
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleInvoiceCreated = (invoice) => {
    // Reload entries to update invoiced status
    loadEntries();
    loadPrepaidInfo();
    loadCaseInvoices();
    onRefresh?.();
    setShowInvoiceModal(false);
    setInvoiceInitialData(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const totalHours = entries.reduce((sum, entry) => sum + entry.hours, 0);
  const totalAmount = entries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
  const billableHours = entries.filter(e => e.billable).reduce((sum, entry) => sum + entry.hours, 0);

  // Invoice summary stats
  const invoiceTotalBilled = caseInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const invoiceTotalPaid = caseInvoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0);
  const invoiceTotalDue = caseInvoices.reduce((sum, inv) => sum + (inv.amount_due || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-xl p-4">
          <p className="text-sm text-blue-300">Total Hours</p>
          <p className="text-3xl font-bold text-white">{totalHours.toFixed(1)}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-4">
          <p className="text-sm text-green-300">Billable Hours</p>
          <p className="text-3xl font-bold text-white">{billableHours.toFixed(1)}</p>
        </div>
        <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4">
          <p className="text-sm text-cyan-300">Total Amount</p>
          <p className="text-3xl font-bold text-white">${totalAmount.toLocaleString()}</p>
        </div>
      </div>

      {/* Prepaid Hours Section */}
      {!prepaidLoading && (
        <div className="mb-6">
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg font-semibold text-white">Prepaid Hours</h3>
              </div>
              {!showPurchaseForm && (
                <button
                  onClick={() => setShowPurchaseForm(true)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-lg hover:bg-purple-500/30 transition-colors"
                >
                  <Plus size={16} />
                  Add Hours
                </button>
              )}
            </div>

            {/* Balance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-virgo-navy-800/50 rounded-lg p-3 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-purple-300">Hours Remaining</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {prepaidInfo?.hours_remaining?.toFixed(1) || '0.0'}
                </p>
              </div>
              <div className="bg-virgo-navy-800/50 rounded-lg p-3 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-300">Balance</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  ${prepaidInfo?.balance_dollars?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                </p>
              </div>
              <div className="bg-virgo-navy-800/50 rounded-lg p-3 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-cyan-300">Purchased Rate</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {prepaidInfo?.purchased_rate ? `$${prepaidInfo.purchased_rate}/hr` : '—'}
                </p>
              </div>
            </div>

            {/* Purchase Form */}
            {showPurchaseForm && (
              <div className="bg-virgo-navy-800/50 rounded-lg p-4 border border-purple-500/30 mb-4">
                <h4 className="text-sm font-medium text-purple-300 mb-3">Add Prepaid Hours</h4>
                <form onSubmit={handlePurchasePrepaid} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-virgo-teal-400 mb-1">
                        Hours *
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        required
                        value={purchaseFormData.hours}
                        onChange={(e) => setPurchaseFormData({ ...purchaseFormData, hours: e.target.value })}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-purple-500 text-sm"
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-virgo-teal-400 mb-1">
                        Hourly Rate *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={purchaseFormData.hourly_rate}
                        onChange={(e) => setPurchaseFormData({ ...purchaseFormData, hourly_rate: e.target.value })}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-purple-500 text-sm"
                        placeholder="250.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-virgo-teal-400 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={purchaseFormData.description}
                        onChange={(e) => setPurchaseFormData({ ...purchaseFormData, description: e.target.value })}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-purple-500 text-sm"
                        placeholder="Prepaid hours purchase"
                      />
                    </div>
                  </div>
                  {purchaseFormData.hours && purchaseFormData.hourly_rate && (
                    <div className="text-sm text-purple-300">
                      Total: <span className="font-semibold text-white">${(parseFloat(purchaseFormData.hours) * parseFloat(purchaseFormData.hourly_rate)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPurchaseForm(false)}
                      className="px-3 py-1.5 text-sm text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={purchaseSubmitting}
                      className="px-3 py-1.5 text-sm text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:from-purple-600 hover:to-pink-600 disabled:opacity-50"
                    >
                      {purchaseSubmitting ? 'Adding...' : 'Add Prepaid Hours'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Transaction History Toggle */}
            {prepaidInfo?.transactions?.length > 0 && (
              <div>
                <button
                  onClick={() => setShowTransactionHistory(!showTransactionHistory)}
                  className="flex items-center gap-2 text-sm text-purple-300 hover:text-purple-200 transition-colors"
                >
                  {showTransactionHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {showTransactionHistory ? 'Hide' : 'Show'} Transaction History ({prepaidInfo.transactions.length})
                </button>

                {showTransactionHistory && (
                  <div className="mt-3 bg-virgo-navy-800/50 rounded-lg border border-purple-500/20 overflow-hidden">
                    <table className="min-w-full divide-y divide-virgo-navy-600">
                      <thead className="bg-virgo-navy-800/50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Type</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Description</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Hours</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-virgo-navy-600">
                        {prepaidInfo.transactions.map((tx, idx) => (
                          <tr key={idx} className="hover:bg-virgo-navy-600/50">
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                              {tx.created_at ? formatDate(tx.created_at) : '—'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                tx.transaction_type === 'purchase'
                                  ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                  : 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                              }`}>
                                {tx.transaction_type === 'purchase' ? 'Purchase' : 'Deduction'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-virgo-teal-200">
                              {tx.description || '—'}
                            </td>
                            <td className={`px-4 py-2 whitespace-nowrap text-sm text-right ${
                              tx.transaction_type === 'purchase' ? 'text-green-400' : 'text-orange-400'
                            }`}>
                              {tx.transaction_type === 'purchase' ? '+' : '-'}{tx.hours?.toFixed(1) || '0.0'}
                            </td>
                            <td className={`px-4 py-2 whitespace-nowrap text-sm text-right ${
                              tx.transaction_type === 'purchase' ? 'text-green-400' : 'text-orange-400'
                            }`}>
                              {tx.transaction_type === 'purchase' ? '+' : '-'}${tx.amount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* No prepaid notice */}
            {(!prepaidInfo?.enabled && !prepaidInfo?.transactions?.length) && (
              <p className="text-sm text-virgo-teal-400 text-center py-2">
                No prepaid hours purchased yet. Click "Add Hours" to set up a prepaid balance.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Add Time Entry & Generate Invoice */}
      <div className="mb-6 flex flex-wrap gap-3">
        {!showAddForm ? (
          <>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-medium shadow-lg shadow-cyan-500/30 transition-all"
            >
              + Add Time Entry
            </button>
            <button
              onClick={handleGenerateInvoice}
              disabled={invoiceLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 font-medium shadow-lg shadow-green-500/30 transition-all disabled:opacity-50"
            >
              <FileText size={18} />
              {invoiceLoading ? 'Loading...' : 'Generate Invoice'}
            </button>
          </>
        ) : (
          <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                    Hours *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={formData.hours}
                    onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                    placeholder="2.5"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                  Description *
                </label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                  placeholder="Describe the work performed..."
                />
              </div>

              {/* UTBMS Codes Section */}
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileCode className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm font-medium text-cyan-300">LEDES E-Billing Codes</span>
                  <div className="group relative">
                    <Info className="w-4 h-4 text-cyan-400 cursor-help" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-virgo-navy-600 border border-cyan-500/30 rounded-lg text-xs text-virgo-teal-300 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      UTBMS codes are required for LEDES e-billing exports. Select a task code and activity code to categorize this time entry for corporate client invoices.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                      Task Code
                    </label>
                    <select
                      value={formData.utbms_task_code}
                      onChange={(e) => setFormData({ ...formData, utbms_task_code: e.target.value })}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="">Select task code...</option>
                      {utbmsCodes.task_codes.map((code) => (
                        <option key={code.code} value={code.code}>
                          {code.code} - {code.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                      Activity Code
                    </label>
                    <select
                      value={formData.utbms_activity_code}
                      onChange={(e) => setFormData({ ...formData, utbms_activity_code: e.target.value })}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="">Select activity code...</option>
                      {utbmsCodes.activity_codes.map((code) => (
                        <option key={code.code} value={code.code}>
                          {code.code} - {code.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.billable}
                  onChange={(e) => setFormData({ ...formData, billable: e.target.checked })}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-virgo-teal-700 rounded bg-virgo-navy-600"
                />
                <label className="ml-2 block text-sm text-virgo-teal-300">
                  Billable
                </label>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
                >
                  {submitting ? 'Adding...' : 'Add Entry'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Time Entries Table */}
      {entries.length === 0 ? (
        <div className="text-center py-12 text-virgo-teal-400">
          No time entries yet. Add your first entry above.
        </div>
      ) : (
        <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl overflow-hidden">
          <table className="min-w-full divide-y divide-virgo-navy-600">
            <thead className="bg-virgo-navy-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Hours</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-virgo-navy-600">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-virgo-navy-600/50 transition-colors">
                  {editingEntry === entry.id ? (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="date"
                          value={editFormData.date}
                          onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                          className="w-32 px-2 py-1 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-sm"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="text"
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          className="w-full px-2 py-1 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          step="0.1"
                          value={editFormData.hours}
                          onChange={(e) => setEditFormData({ ...editFormData, hours: e.target.value })}
                          className="w-20 px-2 py-1 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          step="0.01"
                          value={editFormData.hourly_rate}
                          onChange={(e) => setEditFormData({ ...editFormData, hourly_rate: e.target.value })}
                          className="w-24 px-2 py-1 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-sm"
                          placeholder="Rate"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                        {editFormData.hours && editFormData.hourly_rate
                          ? `$${(parseFloat(editFormData.hours) * parseFloat(editFormData.hourly_rate)).toFixed(2)}`
                          : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editFormData.billable}
                            onChange={(e) => setEditFormData({ ...editFormData, billable: e.target.checked })}
                            className="h-4 w-4 text-cyan-600 border-virgo-teal-700 rounded bg-virgo-navy-600"
                          />
                          <span className="ml-2 text-xs text-virgo-teal-300">Billable</span>
                        </label>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleSaveEdit(entry.id)}
                            disabled={submitting}
                            className="p-1 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded"
                            title="Save"
                          >
                            <Check size={18} />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded"
                            title="Cancel"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {formatDate(entry.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-teal-200">
                        {entry.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {entry.hours.toFixed(1)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-300">
                        {entry.rate ? `$${entry.rate.toFixed(2)}/hr` : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                        {entry.amount ? `$${entry.amount.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {entry.billable && (
                            <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-300 border border-green-500/30 rounded">
                              Billable
                            </span>
                          )}
                          {entry.invoiced && (
                            <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded">
                              Invoiced
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(entry)}
                            className="p-1 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20 rounded"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          {!entry.invoiced && (
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ========== INVOICES & PAYMENTS SECTION ========== */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Invoices & Payments</h3>
          </div>
        </div>

        {/* Invoice Summary Stats */}
        {caseInvoices.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-virgo-navy-800/50 rounded-lg p-3 border border-green-500/20">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-300">Total Billed</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(invoiceTotalBilled)}</p>
            </div>
            <div className="bg-virgo-navy-800/50 rounded-lg p-3 border border-cyan-500/20">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-300">Total Paid</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(invoiceTotalPaid)}</p>
            </div>
            <div className="bg-virgo-navy-800/50 rounded-lg p-3 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-amber-300">Outstanding</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(invoiceTotalDue)}</p>
            </div>
          </div>
        )}

        {invoicesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
          </div>
        ) : caseInvoices.length === 0 ? (
          <div className="text-center py-8 text-virgo-teal-400 bg-virgo-navy-800/30 rounded-xl border border-virgo-teal-700/20">
            No invoices for this case yet. Generate one from unbilled time entries above.
          </div>
        ) : (
          <div className="space-y-3">
            {caseInvoices.map((invoice) => (
              <div key={invoice.id} className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl overflow-hidden">
                {/* Invoice Row */}
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <button
                      onClick={() => toggleInvoiceExpand(invoice.id)}
                      className="text-virgo-teal-400 hover:text-white transition-colors flex-shrink-0"
                    >
                      {expandedInvoice === invoice.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-white">{invoice.invoice_number}</span>
                        <span className={`px-2 py-0.5 text-xs rounded border ${statusColors[invoice.status] || statusColors.draft}`}>
                          {invoice.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-virgo-teal-400">
                        <span>Issued: {invoice.invoice_date ? formatDate(invoice.invoice_date) : '—'}</span>
                        <span>Due: {invoice.due_date ? formatDate(invoice.due_date) : '—'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-semibold text-white">{formatCurrency(invoice.total_amount)}</div>
                      {invoice.amount_paid > 0 && (
                        <div className="text-xs text-green-400">Paid: {formatCurrency(invoice.amount_paid)}</div>
                      )}
                      {invoice.amount_due > 0 && invoice.status !== 'paid' && (
                        <div className="text-xs text-amber-400">Due: {formatCurrency(invoice.amount_due)}</div>
                      )}
                    </div>
                    {invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.amount_due > 0 && (
                      <button
                        onClick={() => openPaymentModal(invoice)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
                      >
                        <CreditCard size={14} />
                        Record Payment
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded: Payment History */}
                {expandedInvoice === invoice.id && (
                  <div className="border-t border-virgo-navy-600 px-5 py-4 bg-virgo-navy-900/30">
                    <h4 className="text-sm font-medium text-virgo-teal-300 mb-3">Payment History</h4>
                    {invoicePayments[invoice.id] === undefined ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-400"></div>
                      </div>
                    ) : invoicePayments[invoice.id]?.length === 0 ? (
                      <p className="text-sm text-virgo-teal-500 py-2">No payments recorded yet.</p>
                    ) : (
                      <div className="overflow-hidden rounded-lg border border-virgo-navy-600">
                        <table className="min-w-full divide-y divide-virgo-navy-600">
                          <thead className="bg-virgo-navy-800/50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Date</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Method</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Reference</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Notes</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-virgo-navy-600">
                            {invoicePayments[invoice.id].map((pmt) => (
                              <tr key={pmt.id} className="hover:bg-virgo-navy-600/50">
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                                  {pmt.payment_date ? formatDate(pmt.payment_date) : '—'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-virgo-teal-200 capitalize">
                                  {pmt.payment_method?.replace('_', ' ') || '—'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-virgo-teal-300">
                                  {pmt.reference_number || '—'}
                                </td>
                                <td className="px-4 py-2 text-sm text-virgo-teal-400 max-w-xs truncate">
                                  {pmt.notes || '—'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-green-400 font-medium">
                                  {formatCurrency(pmt.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Record Payment Modal */}
      {showPaymentModal && paymentInvoice && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-virgo-navy-600 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Record Payment</h3>
                <p className="text-sm text-virgo-teal-400 mt-1">
                  {paymentInvoice.invoice_number} — Balance: {formatCurrency(paymentInvoice.amount_due)}
                </p>
              </div>
              <button
                onClick={() => { setShowPaymentModal(false); setPaymentInvoice(null); }}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Payment Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="w-full pl-8 pr-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Payment Method *</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="check">Check</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="bank_transfer">Bank Transfer / ACH</option>
                  <option value="wire">Wire Transfer</option>
                  <option value="money_order">Money Order</option>
                  <option value="square">Square</option>
                  <option value="other">Other</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Payment Date *</label>
                <input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Reference / Check Number</label>
                <input
                  type="text"
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Check #1234"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Notes</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500 resize-none"
                  placeholder="Optional payment notes..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowPaymentModal(false); setPaymentInvoice(null); }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingPayment}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {submittingPayment ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Recording...
                    </>
                  ) : (
                    'Record Payment'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setInvoiceInitialData(null);
        }}
        onInvoiceCreated={handleInvoiceCreated}
        initialCaseId={invoiceInitialData?.caseId}
        initialContactId={invoiceInitialData?.contactId}
        initialContactName={invoiceInitialData?.contactName}
        initialLineItems={invoiceInitialData?.lineItems}
      />
    </div>
  );
};

export default CaseTime;
